import type { ClaimPreview } from '@pos/api-client';
import { AuthHeading, AuthScreen, Notice } from '@pos/ui';
import { useEffect, useState } from 'react';
import { claimWithTelegram, previewClaim, telegramErrorText } from './auth';
import { TelegramSignInButton } from './SignIn';

/**
 * «Войти в приложение владельца» — the one-time link an owner (or the Gertsak
 * team) sends to a manager or a new client. Opening it and signing in with
 * Telegram attaches that Telegram to the prepared account. The link alone
 * signs in nobody.
 */
export function Claim({ token, onDone }: { token: string; onDone: () => void }) {
  const [preview, setPreview] = useState<ClaimPreview | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    previewClaim(token)
      .then((p) => alive && setPreview(p))
      .catch((e: unknown) => alive && setError(telegramErrorText(e)));
    return () => {
      alive = false;
    };
  }, [token]);

  return (
    <AuthScreen
      footer={
        <button type="button" className="py-2 text-base text-primary" onClick={onDone}>
          Обычный вход
        </button>
      }
    >
      {error ? (
        <>
          <AuthHeading title="Ссылка не работает" />
          <Notice tone="danger">{error}</Notice>
        </>
      ) : !preview ? (
        <div className="h-40 animate-pulse rounded-xl bg-sunken" aria-busy aria-label="Загрузка" />
      ) : (
        <>
          <AuthHeading
            title={`Здравствуйте, ${preview.name}`}
            subtitle={
              preview.businessName
                ? `Вас пригласили в «${preview.businessName}»${preview.roleName ? ` — ${preview.roleName}` : ''}.`
                : 'Вас пригласили в Gertsak.'
            }
          />
          {preview.alreadyLinked ? (
            <Notice tone="info">
              К этому аккаунту уже привязан Telegram — войдите через него.
            </Notice>
          ) : (
            <p className="text-base text-ink-2">
              Войдите через Telegram — он станет вашим входом в Gertsak. Пароль не нужен.
            </p>
          )}
          <TelegramSignInButton
            onToken={async (idToken, nonce) => {
              try {
                await claimWithTelegram(token, idToken, nonce);
                onDone();
                return null;
              } catch (e) {
                return telegramErrorText(e);
              }
            }}
          />
          <p className="text-sm text-ink-3">
            Ссылка действует до {new Date(preview.expiresAt).toLocaleDateString('ru-RU')} и работает
            один раз.
          </p>
        </>
      )}
    </AuthScreen>
  );
}
